import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  type: 'enterprise' | 'smb' | 'small-business' | 'startup';
  status: 'active' | 'inactive';
  address?: string;
  city?: string;
  country?: string;
  taxId?: string;
  // Note: These might not be returned directly by the backend unless aggregated, but we default them if absent.
  invoices?: number;
  revenue?: number;
  createdAt: string;
}

interface CustomerState {
  customers: Customer[];
  stats: any | null;
  isLoading: boolean;
  error: string | null;
  
  fetchCustomers: (filters?: any) => Promise<void>;
  fetchStats: () => Promise<void>;
  createCustomer: (data: Partial<Customer>) => Promise<void>;
}

export const useCustomerStore = create<CustomerState>((set) => ({
  customers: [],
  stats: null,
  isLoading: false,
  error: null,

  fetchCustomers: async (filters?: any) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/money/customers', { params: filters });
      const { data } = response.data;
      
      const mapped = data.map((c: any) => ({
        ...c,
        invoices: c.invoices || 0,
        revenue: c.revenue || 0
      }));

      set({ customers: mapped, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await apiClient.get('/money/customers/stats');
      set({ stats: response.data });
    } catch (error: any) {
      console.error('Failed to fetch customer stats', error);
    }
  },

  createCustomer: async (data: Partial<Customer>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post('/money/customers', data);
      const newCustomer = {
        ...response.data,
        invoices: 0,
        revenue: 0
      };
      set((state) => ({
        customers: [newCustomer, ...state.customers],
        isLoading: false
      }));
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  }
}));
